import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Radio, Trophy, Activity, ArrowRight, Sparkles,
  Image as ImageIcon,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { visionAPI, tournamentsAPI, imagecompoAPI } from '@/lib/api'

/**
 * LiveNowHero — top-of-home widget die live activiteit samenvat.
 * Toont alleen tegels waar iets echt gaande is.
 * Faalt gracefully: 1 API fout dooft niet de rest.
 *
 * Kassa+Announcement zitten bewust NIET in deze hero (staan in eigen widgets
 * onderaan). Bijhorende fetches zijn verwijderd om onnodige API-load te vermijden.
 */
export default function LiveNowHero() {
  const [tiles, setTiles] = useState({
    vision:       { loading: true, data: null },
    imagecompo:   { loading: true, data: null },
    tournament:   { loading: true, data: null },
  })

  const loadAll = useCallback(() => {
    visionAPI.getActiveShow()
      .then(r => setTiles(t => ({ ...t, vision: { loading: false, data: r.data.show } })))
      .catch(() => setTiles(t => ({ ...t, vision: { loading: false, data: null } })))

    imagecompoAPI.getActiveShow()
      .then(r => {
        const show = r.data?.show
        setTiles(t => ({ ...t, imagecompo: { loading: false, data: show ? { show, state: r.data.state } : null } }))
      })
      .catch(() => setTiles(t => ({ ...t, imagecompo: { loading: false, data: null } })))

    tournamentsAPI.getTournaments()
      .then(r => {
        const list = r.data?.tournaments ?? r.data ?? []
        const active = list.find(t =>
          t.status === 'active' || t.status === 'in_progress' || t.status === 'ongoing'
        )
        setTiles(t => ({ ...t, tournament: { loading: false, data: active ?? null } }))
      })
      .catch(() => setTiles(t => ({ ...t, tournament: { loading: false, data: null } })))
  }, [])

  useEffect(() => {
    loadAll()
    // Auto-refresh elke 30s + bij tab-focus zodat "Wat leeft er nu"
    // niet stilstaat als je even weg was.
    const t = setInterval(loadAll, 30_000)
    const onVis = () => { if (document.visibilityState === 'visible') loadAll() }
    document.addEventListener('visibilitychange', onVis)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis) }
  }, [loadAll])

  const anyLoading = Object.values(tiles).some(t => t.loading)
  const anyData    = Object.values(tiles).some(t => !t.loading && t.data)

  if (!anyLoading && !anyData) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-6 text-center">
          <Activity className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Er is momenteel niks live. Wacht op de volgende LAN-meeting.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <span className="live-dot">Wat leeft er nu</span>
      </div>
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <VisionTile      state={tiles.vision} />
        <ImageCompoTile  state={tiles.imagecompo} />
        <TournamentTile  state={tiles.tournament} />
      </div>
    </div>
  )
}

/* ============ Tile primitives ============ */

/**
 * BaseTile — kabinet-stijl icon-tile: bordered card, acid icon, hover → acid border.
 * Als `hot` true is: subtle acid border-left + acid icon-tile.
 */
function BaseTile({ href, icon: Icon, eyebrow, title, subtitle, badge, hot }) {
  const inner = (
    <CardContent className="p-4 flex items-start gap-3 h-full">
      <div className={`h-10 w-10 rounded-[4px] border flex items-center justify-center shrink-0 bg-card
                       ${hot ? 'border-acid/60' : 'border-border'}`}>
        <Icon className={`h-5 w-5 ${hot ? 'text-acid' : 'text-muted-foreground'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground truncate">{eyebrow}</span>
          {badge && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-acid">
              <span className="w-1 h-1 rounded-full bg-acid" />{badge}
            </span>
          )}
        </div>
        <div className="font-semibold text-sm truncate mt-0.5">{title}</div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      {href && <ArrowRight className="h-4 w-4 text-muted-foreground self-center shrink-0" />}
    </CardContent>
  )
  const cls = `transition-colors hover:border-acid h-full ${hot ? 'border-acid' : 'border-border'}`
  const style = hot ? { borderLeft: '2px solid rgb(var(--acid-rgb))' } : {}
  return href
    ? <Link to={href} className="block no-underline"><Card className={cls} style={style}>{inner}</Card></Link>
    : <Card className={cls} style={style}>{inner}</Card>
}

function Skeleton() {
  return (
    <Card><CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-[4px] bg-muted animate-pulse shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-16 bg-muted animate-pulse rounded" />
          <div className="h-4 w-32 bg-muted animate-pulse rounded" />
        </div>
      </div>
    </CardContent></Card>
  )
}

/* ============ Specific tiles ============ */

const KV_PHASE_LABELS = {
  setup: 'Setup', live_songs: 'Live nummers', jury_reveal: 'Jury reveal',
  public_vote: 'Publiek stemt', public_reveal: 'Publiek reveal', finished: 'Klaar',
}
const IC_PHASE_LABELS = {
  draft: 'Draft', submissions: 'Inschrijvingen open', live: 'Show bezig',
  pauze: 'Pauze', reveal: 'Reveal', done: 'Klaar',
}

function VisionTile({ state }) {
  if (state.loading) return <Skeleton />
  if (!state.data) return null
  const show = state.data
  const isLive = !['setup', 'finished'].includes(show.phase)
  return (
    <BaseTile
      href="/kempenvision"
      icon={Radio}
      hot={isLive}
      eyebrow="Kempenvision"
      title={show.name + (show.edition ? ` · ${show.edition}` : '')}
      subtitle={KV_PHASE_LABELS[show.phase] ?? show.phase}
      badge={isLive ? 'Live' : null}
    />
  )
}

function ImageCompoTile({ state }) {
  if (state.loading) return <Skeleton />
  if (!state.data?.show) return null
  const { show } = state.data
  const isLive = !['draft', 'done'].includes(show.phase)
  const isOpen = show.submissions_open
  return (
    <BaseTile
      href="/imagecompo"
      icon={ImageIcon}
      hot={isLive}
      eyebrow="Image Compo"
      title={show.name + (show.edition ? ` · ${show.edition}` : '')}
      subtitle={IC_PHASE_LABELS[show.phase] ?? show.phase}
      badge={isLive ? 'Live' : (isOpen ? 'Open' : null)}
    />
  )
}

function KassaTile({ state }) {
  if (state.loading) return <Skeleton />
  if (!state.data?.event) return null
  const { event, top } = state.data
  return (
    <BaseTile
      href="/bestellen"
      icon={ShoppingBag}
      hot={!!event}
      eyebrow="Bar & bestellen"
      title={event.name || 'Bar open'}
      subtitle={top?.customer?.name ? `Top: ${top.customer.name}` : 'Klaar om te tappen'}
    />
  )
}

function TournamentTile({ state }) {
  if (state.loading) return <Skeleton />
  if (!state.data) return null
  const t = state.data
  return (
    <BaseTile
      href={`/tournaments/${t.id}`}
      icon={Trophy}
      hot
      eyebrow={t.game || 'Tournament'}
      title={t.name}
      subtitle="Live bezig"
      badge="Live"
    />
  )
}

function AnnouncementTile({ state }) {
  if (state.loading) return <Skeleton />
  if (!state.data) return null
  const a = state.data
  return (
    <BaseTile
      href="/events"
      icon={Megaphone}
      eyebrow="Aankondiging"
      title={a.title}
      subtitle={a.summary || null}
    />
  )
}
